import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import { useState, type ReactElement } from 'react'

import { ScrollableDataRegion } from '~/components/data/ScrollableDataRegion'
import {
  syncStatusQuery,
  useSyncFromStripe,
} from '~/features/subscriptions'
import { logger } from '~/lib/logger'

/**
 * `/admin/subscriptions/sync` — Sync from Stripe (HANDOFF §4).
 *
 * Runs `POST /sync-from-stripe` and surfaces the resulting diff as a
 * status table. The button is the canonical sync trigger; the
 * "Ensure Stripe Products" CTA is duplicated on the Overview page.
 */
export const Route = createFileRoute('/admin/subscriptions/sync')({
  component: SyncFromStripePage,
})

function SyncFromStripePage(): ReactElement {
  const status = useQuery(syncStatusQuery())
  const syncMutation = useSyncFromStripe()
  const [log, setLog] = useState<ReadonlyArray<string>>([])

  const runSync = (): void => {
    syncMutation.mutate(undefined, {
      onSuccess: (result) => {
        setLog((prev) => [
          ...prev,
          `${new Date().toISOString().slice(11, 19)} · created: ${result.created}, updated: ${result.updated}, unchanged: ${result.unchanged}`,
        ])
      },
      onError: (cause) => {
        logger.warn('stripe sync failed', { cause })
        setLog((prev) => [
          ...prev,
          `${new Date().toISOString().slice(11, 19)} · failed`,
        ])
      },
    })
  }

  const rows = status.data?.rows ?? []
  const lastSyncedAt = status.data?.last_synced_at ?? null

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Sync from Stripe</h1>
          <p className="ka-page-header-subtitle">
            {lastSyncedAt
              ? `Last sync ${lastSyncedAt.slice(0, 19).replace('T', ' ')}`
              : 'Never synced'}
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            disabled={syncMutation.isPending}
            onClick={runSync}
          >
            <RefreshCw size={14} aria-hidden="true" />{' '}
            {syncMutation.isPending ? 'Running…' : 'Run Sync'}
          </button>
        </div>
      </header>

      <div className="ka-card ka-card--flush ka-page-section ka-subs-table-card">
        <ScrollableDataRegion label="Stripe sync status" minWidth="840px">
          <table className="ka-table">
          <thead>
            <tr>
              <th scope="col">Stripe ID</th>
              <th scope="col">Stripe name</th>
              <th scope="col">Local plan</th>
              <th scope="col">Status</th>
              <th scope="col">Last sync</th>
            </tr>
          </thead>
          <tbody>
            {status.isLoading ? (
              <tr>
                <td colSpan={5} className="ka-table-empty" aria-busy="true">
                  Loading sync status…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="ka-table-empty">
                  Run a sync to populate this table.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.stripe_product_id}>
                  <td className="ka-mono-meta">
                    {r.stripe_product_id}
                  </td>
                  <td>{r.stripe_name ?? '—'}</td>
                  <td>{r.local_plan_name ?? '—'}</td>
                  <td>
                    <SyncStatusChip status={r.status} />
                  </td>
                  <td className="ka-td-meta">
                    {r.last_sync_at?.slice(0, 19).replace('T', ' ') ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </ScrollableDataRegion>
      </div>

      {log.length > 0 ? (
        <div className="ka-card ka-page-section ka-subs-table-card">
          <div className="ka-card-head">
            <span className="ka-card-head-title">Sync log</span>
          </div>
          <ul className="ka-log-list">
            {log.map((entry, i) => (
              <li key={i}>{entry}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function SyncStatusChip({
  status,
}: {
  readonly status:
    | 'in_sync'
    | 'out_of_sync'
    | 'local_only'
    | 'stripe_only'
    | 'missing'
}): ReactElement {
  if (status === 'in_sync')
    return <span className="ka-chip ka-chip--success">In sync</span>
  if (status === 'out_of_sync')
    return <span className="ka-chip ka-chip--warning">Out of sync</span>
  if (status === 'local_only')
    return <span className="ka-chip ka-chip--info">Local only</span>
  if (status === 'stripe_only')
    return <span className="ka-chip ka-chip--info">Stripe only</span>
  return <span className="ka-chip ka-chip--destructive">Missing</span>
}
